import type { UserRolePermissions } from '@/services/userRoleService';
import { permissionCategories } from '../permissionCategories';
import {
  buildPermissionStateFromData,
  createDefaultPermissionState,
  type PermissionState,
} from './permissionState';

export type PermissionSnapshot = Record<string, boolean | string>;

export interface PermissionDiff {
  added: string[];
  removed: string[];
}

export const PERMISSION_LABELS: Record<string, string> = {
  viewTimesheetsAccess: 'View Timesheets Access',
  editTimesheetsAccess: 'Edit Timesheets Access',
  allowDeleteTimesheets: 'Delete Timesheets',
  allowDuplicateTimesheets: 'Duplicate Timesheets',
  allowRecallTimesheet: 'Recall Timesheet',
  enableCopyFromLastWeek: 'Copy From Last Week',
  enableCopyDescription: 'Copy Description',
  enableCopyOnlyBillable: 'Copy Only Billable',
  enableAutoSmartFill: 'Auto Smart Fill',
  enableAiInsights: 'AI Insights',
  viewApprovalDashboard: 'View Approval Dashboard',
  viewApprovals: 'View Approvals',
  editTimesheetsApproval: 'Edit Timesheets (Approval)',
  rejectTimesheets: 'Reject Timesheets',
  compulsoryRejectionReason: 'Compulsory Rejection Reason',
  bulkApprove: 'Bulk Approve & Reject',
  delegateApproval: 'Delegate Approval',
  viewPolicyViolations: 'View Policy Violations',
  viewDetectedIssues: 'View Detected Issues',
  enableTimesheetReport: 'Timesheet Report',
  allowTimesheetReportExport: 'Timesheet Report Export',
  enableMissingTimesheetReport: 'Missing Timesheet Report',
  allowMissingTimesheetReportExport: 'Missing Timesheet Report Export',
  enableUtilisationReport: 'Utilisation Report',
  allowUtilisationReportExport: 'Utilisation Report Export',
  viewEmployeeDirectory: 'View Employee Directory',
  viewEmployeeDetails: 'View Employee Details',
  viewIntegrations: 'View Integrations',
  manageIntegrations: 'Manage Integrations',
  viewCompanies: 'View Companies',
  manageCompanies: 'Manage Companies',
  manageGeneralSettings: 'Manage General Settings',
  manageDeadlines: 'Manage Deadlines',
  managePolicySettings: 'Manage Policy Settings',
  manageUtilisationSettings: 'Manage Utilisation Settings',
  manageTimeEntrySettings: 'Manage Time Entry Settings',
  manageWorkTypeSettings: 'Manage Work Type Settings',
  manageApprovalsSettings: 'Manage Approvals Settings',
  manageNotifications: 'Manage Notifications',
  manageRolesPermissions: 'Manage Roles & Permissions',
  manageRates: 'Manage Roles & Rates',
  manageScreenDataModes: 'Manage Screen Data Modes',
};

export const getPermissionLabel = (key: string): string => {
  if (key.startsWith('perm:')) {
    const permId = key.slice(5);
    for (const cat of permissionCategories) {
      const allPerms = cat.groups ? cat.groups.flatMap(g => g.permissions) : cat.permissions;
      const found = allPerms.find(p => p.id === permId);
      if (found) return found.name;
    }
    return permId;
  }
  return PERMISSION_LABELS[key] || key;
};

export const buildSnapshotFromState = (permissions: PermissionState): PermissionSnapshot => {
  const snap: PermissionSnapshot = {};
  permissions.enabledPermissions.forEach(p => { snap[`perm:${p}`] = true; });
  snap.viewTimesheetsAccess = permissions.viewTimesheetsAccess;
  snap.editTimesheetsAccess = permissions.editTimesheetsAccess;
  snap.allowDeleteTimesheets = permissions.allowDeleteTimesheets;
  snap.allowDuplicateTimesheets = permissions.allowDuplicateTimesheets;
  snap.allowRecallTimesheet = permissions.allowRecallTimesheet;
  snap.enableCopyFromLastWeek = permissions.enableCopyFromLastWeek;
  snap.enableCopyDescription = permissions.enableCopyDescription;
  snap.enableCopyOnlyBillable = permissions.enableCopyOnlyBillable;
  snap.enableAutoSmartFill = permissions.enableAutoSmartFill;
  snap.enableAiInsights = permissions.enableAiInsights;
  snap.viewApprovalDashboard = permissions.viewApprovalDashboard;
  snap.viewApprovals = permissions.viewApprovals;
  snap.editTimesheetsApproval = permissions.editTimesheetsApproval;
  snap.rejectTimesheets = permissions.rejectTimesheets;
  snap.compulsoryRejectionReason = permissions.compulsoryRejectionReason;
  snap.bulkApprove = permissions.bulkApprove;
  snap.delegateApproval = permissions.delegateApproval;
  snap.viewPolicyViolations = permissions.viewPolicyViolations;
  snap.viewDetectedIssues = permissions.viewDetectedIssues;
  snap.enableTimesheetReport = permissions.enableTimesheetReport;
  snap.allowTimesheetReportExport = permissions.allowTimesheetReportExport;
  snap.enableMissingTimesheetReport = permissions.enableMissingTimesheetReport;
  snap.allowMissingTimesheetReportExport = permissions.allowMissingTimesheetReportExport;
  snap.enableUtilisationReport = permissions.enableUtilisationReport;
  snap.allowUtilisationReportExport = permissions.allowUtilisationReportExport;
  snap.viewEmployeeDirectory = permissions.viewEmployeeDirectory;
  snap.viewEmployeeDirectoryAccess = permissions.viewEmployeeDirectoryAccess;
  snap.viewEmployeeDetails = permissions.viewEmployeeDetails;
  snap.viewIntegrations = permissions.viewIntegrations;
  snap.manageIntegrations = permissions.manageIntegrations;
  snap.viewCompanies = permissions.viewCompanies;
  snap.manageCompanies = permissions.manageCompanies;
  snap.manageGeneralSettings = permissions.manageGeneralSettings;
  snap.manageDeadlines = permissions.manageDeadlines;
  snap.managePolicySettings = permissions.managePolicySettings;
  snap.manageUtilisationSettings = permissions.manageUtilisationSettings;
  snap.manageTimeEntrySettings = permissions.manageTimeEntrySettings;
  snap.manageWorkTypeSettings = permissions.manageWorkTypeSettings;
  snap.manageApprovalsSettings = permissions.manageApprovalsSettings;
  snap.manageNotifications = permissions.manageNotifications;
  snap.manageRolesPermissions = permissions.manageRolesPermissions;
  snap.manageRates = permissions.manageRates;
  snap.manageScreenDataModes = permissions.manageScreenDataModes;
  return snap;
};

/** Snapshot of the role as loaded from the API — the baseline for the save diff. */
export const buildSnapshotFromData = (data: UserRolePermissions): PermissionSnapshot =>
  buildSnapshotFromState({ ...createDefaultPermissionState(), ...buildPermissionStateFromData(data) });

export const computePermissionDiff = (
  current: PermissionSnapshot,
  initial: PermissionSnapshot | null,
): PermissionDiff => {
  if (!initial) return { added: [], removed: [] };
  const added: string[] = [];
  const removed: string[] = [];
  const allKeys = new Set([...Object.keys(initial), ...Object.keys(current)]);
  for (const key of allKeys) {
    const initialVal = initial[key];
    const currentVal = current[key];
    if (key.startsWith('perm:')) {
      if (!initialVal && currentVal) added.push(getPermissionLabel(key));
      else if (initialVal && !currentVal) removed.push(getPermissionLabel(key));
    } else if (key === 'viewTimesheetsAccess' || key === 'editTimesheetsAccess' || key === 'manageGeneralSettings') {
      const wasEnabled = initialVal !== 'none' && initialVal !== 'view';
      const isEnabled = currentVal !== 'none' && currentVal !== 'view';
      if (!wasEnabled && isEnabled) added.push(getPermissionLabel(key));
      else if (wasEnabled && !isEnabled) removed.push(getPermissionLabel(key));
    } else {
      if (!initialVal && currentVal) added.push(getPermissionLabel(key));
      else if (initialVal && !currentVal) removed.push(getPermissionLabel(key));
    }
  }
  return { added, removed };
};
